// src/socket.js

import { io } from "socket.io-client";
import {
  ensureNotificationPermission,
  notifyChat,
  notif,
} from "./lib/notifications";

/* ==========================================================
   SOCKET STATE
========================================================== */

let socket = null;
let _myUserId = null;
let _socketUserId = null;

let dmNotificationHandler = null;
let projectNotificationHandler = null;
let categoryNotificationHandler = null;
let announcementNotificationHandler = null;
let mentionNotificationHandler = null;

const isBrowser = typeof window !== "undefined";

const isLocal =
  isBrowser &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

/* ==========================================================
   SERVER URL
========================================================== */

const ENV_BASE =
  (typeof process !== "undefined" &&
    (process.env.REACT_APP_SERVER_URL ||
      process.env.REACT_APP_BACKEND_URL ||
      process.env.REACT_APP_API_BASE_URL)) ||
  null;

const SOCKET_BASE =
  ENV_BASE?.replace(/\/api\/?$/, "") ||
  (isLocal
    ? "http://localhost:8080"
    : "https://erp-api.sdcllp.co.in");

console.log("🌐 SOCKET BASE:", SOCKET_BASE);

/* ==========================================================
   HELPERS
========================================================== */

const idOf = (value) => {
  return String(value?._id || value || "");
};

const messagePreview = (message = {}) => {
  // 1) Typed message always gets first priority.
  const typedMessage =
    message?.content ||
    message?.text ||
    message?.message ||
    message?.caption ||
    "";

  const cleanText = String(typedMessage || "").trim();

  if (cleanText) {
    return cleanText;
  }

  // 2) If there is no typed message, show the actual document/file name.
  if (message?.fileName) {
    return `📎 ${message.fileName}`;
  }

  // 3) Multiple uploaded files.
  if (
    Array.isArray(message?.files) &&
    message.files.length > 0
  ) {
    const firstFile = message.files[0];

    const firstName =
      firstFile?.fileName ||
      firstFile?.name ||
      firstFile?.originalName ||
      firstFile?.filename;

    if (firstName) {
      return `📎 ${firstName}`;
    }

    return "📎 Document received";
  }

  // 4) Single file object.
  if (message?.file && typeof message.file === "object") {
    const singleFileName =
      message.file?.fileName ||
      message.file?.name ||
      message.file?.originalName ||
      message.file?.filename;

    if (singleFileName) {
      return `📎 ${singleFileName}`;
    }

    if (message.file?.url) {
      return "📎 Document received";
    }
  }

  // 5) Single file URL/string.
  if (message?.file) {
    return "📎 Document received";
  }

  return "New message received";
};

/* ==========================================================
   CONNECT SOCKET
========================================================== */

export const connectSocket = (userId) => {
  if (!userId) {
    console.warn("❌ connectSocket: missing userId");
    return null;
  }

  _myUserId = String(userId);

  console.log("👤 SOCKET USER ID:", _myUserId);

  /* ------------------------------------------
     Reuse existing socket of the SAME user if it is
     connected OR still connecting/reconnecting.
     (Recreating a connecting socket removed all listeners
      and left other code holding a dead socket.)
  ------------------------------------------ */

  if (
    socket &&
    _socketUserId === _myUserId &&
    (socket.connected || socket.active)
  ) {
    console.log(
      "♻️ Reusing socket:",
      socket.id || "(connecting)"
    );

    initSocketNotifications();

    return socket;
  }

  /* ------------------------------------------
     Remove previous socket instance
  ------------------------------------------ */

  if (socket) {
    try {
      removeSocketNotifications();

      socket.removeAllListeners();

      socket.disconnect();
    } catch (err) {
      console.error(
        "Old socket cleanup error:",
        err
      );
    }
  }

  /* ------------------------------------------
     Create socket connection
  ------------------------------------------ */

  _socketUserId = _myUserId;

  socket = io(SOCKET_BASE, {
    path: "/socket.io",
    withCredentials: true,

    transports: ["websocket", "polling"],

    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 800,
    reconnectionDelayMax: 5000,
    timeout: 20000,

    auth: {
      userId,
    },

    query: {
      userId,
    },
  });

  /* ------------------------------------------
     Register global notification listeners
  ------------------------------------------ */

  initSocketNotifications();

  /* ------------------------------------------
     Socket connected
  ------------------------------------------ */

  socket.on("connect", async () => {
    console.log(
      "🔌 Connected:",
      socket.id
    );

    console.log(
      "👤 Joining user room:",
      userId
    );

    socket.emit(
      "join",
      userId
    );

    try {
      const permission =
        await ensureNotificationPermission();

      console.log(
        "🔔 Notification permission:",
        permission
      );
    } catch (err) {
      console.error(
        "Notification permission error:",
        err
      );
    }
  });

  /* ------------------------------------------
     Socket disconnect
  ------------------------------------------ */

  socket.on("disconnect", (reason) => {
    console.log(
      "🔌 Socket disconnected:",
      reason
    );
  });

  /* ------------------------------------------
     Socket connection error
  ------------------------------------------ */

  socket.on("connect_error", (err) => {
    console.error(
      "❌ Socket connect_error:",
      err?.message || err
    );
  });

  /* ------------------------------------------
     Debug every received socket event
  ------------------------------------------ */

  socket.onAny((eventName, ...args) => {
    console.log(
      "📡 SOCKET EVENT:",
      eventName,
      args
    );
  });

  return socket;
};

/* ==========================================================
   GET SOCKET
========================================================== */

export const getSocket = () => socket;

/* ==========================================================
   GLOBAL NOTIFICATION LISTENERS
========================================================== */

export const initSocketNotifications = () => {
  if (!socket) {
    console.warn(
      "⚠️ initSocketNotifications: socket unavailable"
    );

    return;
  }

  console.log(
    "🔔 Registering socket notification listeners..."
  );

  /*
   * Remove only handlers owned by this notification layer.
   * Do NOT remove unrelated listeners.
   */
  removeSocketNotifications();

  /* ======================================================
     DIRECT MESSAGE
  ====================================================== */

  dmNotificationHandler = (message) => {
    console.log(
      "🔥 NEW MESSAGE EVENT RECEIVED:",
      message
    );

    try {
      const isAnnouncement =
        message?.scope === "announcement" ||
        idOf(
          message?.receiverId
        ).startsWith(
          "group:announcements"
        );

      if (isAnnouncement) {
        console.log(
          "ℹ️ Announcement ignored by DM listener"
        );

        return;
      }

      const isCategory =
        message?.scope === "category" ||
        Boolean(
          message?.category ||
          message?.categoryName
        );

      if (isCategory) {
        categoryNotificationHandler?.(message);
        return;
      }

      const senderId =
        idOf(message?.senderId);

      console.log(
        "📨 Sender ID:",
        senderId
      );

      console.log(
        "👤 My User ID:",
        _myUserId
      );

      if (!senderId) {
        console.warn(
          "❌ Message senderId missing"
        );

        return;
      }

      /*
       * Never show notification for own outgoing message.
       */
      if (
        String(senderId) ===
        String(_myUserId)
      ) {
        console.log(
          "ℹ️ Own message ignored"
        );

        return;
      }

      const senderName =
        message?.senderName ||
        message?.senderId?.name ||
        "New Message";

      const body =
        messagePreview(message);

      console.log(
        "🔔 SHOWING NOTIFICATION:",
        {
          senderName,
          body,
        }
      );

      notifyChat({
        title: senderName,

        body,

        tag:
          notif.dmKey(
            senderId
          ),

        route:
          notif.dmRoute(
            senderId
          ),

        icon:
          message?.senderProfilePic ||
          message?.senderId?.profilePic ||
          "/logo192.png",
      });
    } catch (err) {
      console.error(
        "❌ DM notification error:",
        err
      );
    }
  };

  /* ======================================================
     CATEGORY-WISE COMMUNICATION
  ====================================================== */

  categoryNotificationHandler = (payload) => {
    console.log(
      "🏷️ CATEGORY MESSAGE EVENT:",
      payload
    );

    try {
      const msg =
        payload?.message ||
        payload ||
        {};

      const senderId =
        idOf(
          msg?.sender ||
          msg?.senderId
        );

      if (
        senderId &&
        senderId === String(_myUserId)
      ) {
        return;
      }

      const categoryName =
        msg?.categoryName ||
        msg?.category?.name ||
        msg?.category ||
        payload?.categoryName ||
        payload?.category ||
        "Category";

      const senderName =
        msg?.senderName ||
        msg?.sender?.name ||
        msg?.senderId?.name ||
        msg?.senderLabel ||
        "User";

      notifyChat({
        title: `${senderName} • ${categoryName}`,
        body: messagePreview(msg),
        tag: `category:${String(categoryName || "category").toUpperCase()}`,
        route: "/messenger",
        icon:
          msg?.senderProfilePic ||
          msg?.sender?.profilePic ||
          msg?.senderId?.profilePic ||
          "/sdc-notification.png",
      });
    } catch (err) {
      console.error(
        "❌ Category notification error:",
        err
      );
    }
  };

  /* ======================================================
     PROJECT MESSAGE
  ====================================================== */

  projectNotificationHandler = (payload) => {
    console.log(
      "📁 PROJECT MESSAGE EVENT:",
      payload
    );

    try {
      const msg =
        payload?.message ||
        payload ||
        {};

      const senderId =
        idOf(
          msg?.sender ||
          msg?.senderId
        );

      if (
        !senderId ||
        senderId ===
          String(_myUserId)
      ) {
        return;
      }

      const projectId =
        idOf(
          payload?.projectId ||
          msg?.projectId ||
          msg?.project
        );

      const projectName =
        payload?.projectName ||
        msg?.projectName ||
        msg?.project?.name ||
        "Project";

      const senderName =
        msg?.senderName ||
        msg?.sender?.name ||
        msg?.senderId?.name ||
        "";

      notifyChat({
        title: "Project Communication",

        body: `${projectName} : ${senderName}\n${messagePreview(msg)}`,

        tag: notif.projectKey(
          projectId || projectName
        ),

        route: notif.projectRoute(
          projectId
        ),

        icon: "/sdc-notification.png",
      });
    } catch (err) {
      console.error(
        "❌ Project notification error:",
        err
      );
    }
  };

  /* ======================================================
     ANNOUNCEMENT
  ====================================================== */

  announcementNotificationHandler = (msg) => {
    console.log(
      "📢 ANNOUNCEMENT EVENT:",
      msg
    );

    try {
      const senderId =
        idOf(
          msg?.senderId
        );

      if (
        senderId &&
        senderId ===
          String(_myUserId)
      ) {
        return;
      }

      const company =
        String(
          msg?.company ||
          "SDC"
        ).toUpperCase();

      notifyChat({
        title:
          `Announcement (${company})`,

        body:
          messagePreview(msg),

        tag:
          notif.announceKey(
            company
          ),

        route:
          notif.announceRoute(
            company
          ),

        icon:
          "/logo192.png",
      });
    } catch (err) {
      console.error(
        "❌ Announcement notification error:",
        err
      );
    }
  };

  /* ======================================================
     USER MENTION
  ====================================================== */

  mentionNotificationHandler = (mentionMsg) => {
    console.log(
      "📌 MENTION EVENT:",
      mentionMsg
    );

    try {
      const senderId =
        idOf(
          mentionMsg?.senderId
        );

      if (
        senderId &&
        senderId ===
          String(_myUserId)
      ) {
        return;
      }

      const projectName =
        mentionMsg?.project?.name ||
        mentionMsg?.projectName ||
        "Project";

      const senderName =
        mentionMsg?.senderName ||
        mentionMsg?.senderId?.name ||
        "";

      notifyChat({
        title:
          senderName
            ? `${senderName} mentioned you`
            : `Mentioned in ${projectName}`,

        body:
          mentionMsg?.content ||
          mentionMsg?.text ||
          `You were mentioned in ${projectName}`,

        tag:
          `mention:${
            senderId ||
            projectName
          }`,

        route:
          notif.projectRoute(
            mentionMsg?.project?._id ||
            mentionMsg?.projectId
          ),

        icon:
          mentionMsg?.senderProfilePic ||
          mentionMsg?.senderId?.profilePic ||
          "/logo192.png",
      });
    } catch (err) {
      console.error(
        "❌ Mention notification error:",
        err
      );
    }
  };

  /* ======================================================
     REGISTER EVENTS
  ====================================================== */

  socket.on(
    "newMessage",
    dmNotificationHandler
  );

  socket.on(
    "newProjectMessage",
    projectNotificationHandler
  );

  socket.on(
    "project:newMessage",
    projectNotificationHandler
  );

  socket.on(
    "project:message",
    projectNotificationHandler
  );

  socket.on(
    "newCategoryMessage",
    categoryNotificationHandler
  );

  socket.on(
    "category:newMessage",
    categoryNotificationHandler
  );

  socket.on(
    "category:message",
    categoryNotificationHandler
  );

  socket.on(
    "newAnnouncement",
    announcementNotificationHandler
  );

  socket.on(
    "user:mention",
    mentionNotificationHandler
  );

  console.log(
    "✅ Notification listeners registered"
  );
};

/* ==========================================================
   REMOVE ONLY NOTIFICATION LISTENERS
========================================================== */

export const removeSocketNotifications = () => {
  if (!socket) return;

  if (dmNotificationHandler) {
    socket.off(
      "newMessage",
      dmNotificationHandler
    );

    dmNotificationHandler = null;
  }

  if (projectNotificationHandler) {
    socket.off(
      "newProjectMessage",
      projectNotificationHandler
    );

    socket.off(
      "project:newMessage",
      projectNotificationHandler
    );

    socket.off(
      "project:message",
      projectNotificationHandler
    );

    projectNotificationHandler = null;
  }

  if (categoryNotificationHandler) {
    socket.off(
      "newCategoryMessage",
      categoryNotificationHandler
    );

    socket.off(
      "category:newMessage",
      categoryNotificationHandler
    );

    socket.off(
      "category:message",
      categoryNotificationHandler
    );

    categoryNotificationHandler = null;
  }

  if (
    announcementNotificationHandler
  ) {
    socket.off(
      "newAnnouncement",
      announcementNotificationHandler
    );

    announcementNotificationHandler =
      null;
  }

  if (mentionNotificationHandler) {
    socket.off(
      "user:mention",
      mentionNotificationHandler
    );

    mentionNotificationHandler = null;
  }
};

/* ==========================================================
   EXPORT NOTIFICATION PERMISSION HELPER
========================================================== */

export {
  ensureNotificationPermission,
};